/**
 * Gift Registries
 * Doc: docs/overall-plan/02-MANGO-PERSONAL/gift-registries/
 * Uses backend API when logged in as personal user; falls back to localStorage.
 */
import React, { useState, useEffect } from 'react';
import { giftRegistryApi } from '../services/api';
import { showToast, askConfirm } from 'shared-ui';

const STORAGE_KEY = 'mango_gift_registries';
const EVENT_TYPES = ['Wedding', 'Baby', 'Birthday', 'Housewarming', 'Other'] as const;

interface RegistryItem {
  id: string;
  name: string;
  quantity: number;
  notes: string;
  purchased?: boolean;
}

interface GiftRegistry {
  id: string;
  eventType: string;
  eventDate: string;
  title: string;
  description: string;
  items: RegistryItem[];
  isActive: boolean;
  createdAt: string;
}

const defaultForm = {
  eventType: 'Birthday' as const,
  eventDate: new Date().toISOString().split('T')[0],
  title: '',
  description: '',
  items: [{ id: '1', name: '', quantity: 1, notes: '' }],
};

function normalizeApiRegistry(r: any): GiftRegistry {
  return {
    id: r.id,
    eventType: r.eventType || 'Other',
    eventDate: typeof r.eventDate === 'string' ? r.eventDate.split('T')[0] : new Date(r.eventDate).toISOString().split('T')[0],
    title: r.title || '',
    description: r.description || '',
    items: (r.items || []).map((i: any) => ({ id: i.id, name: i.name, quantity: i.quantity ?? 1, notes: i.notes || '', purchased: i.purchased })),
    isActive: r.isActive !== false,
    createdAt: r.createdAt || new Date().toISOString(),
  };
}

const GiftRegistries: React.FC = () => {
  const [registries, setRegistries] = useState<GiftRegistry[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [useApi, setUseApi] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const loadData = React.useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    try {
      const res: any = await giftRegistryApi.list();
      if (res?.success && Array.isArray(res?.data)) {
        setRegistries(res.data.map(normalizeApiRegistry));
        setUseApi(true);
      } else {
        try {
          const raw = localStorage.getItem(STORAGE_KEY);
          if (raw) {
            const parsed = JSON.parse(raw);
            setRegistries(Array.isArray(parsed) ? parsed : []);
          } else {
            setRegistries([]);
          }
        } catch {
          setRegistries([]);
        }
        setUseApi(false);
      }
    } catch {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) {
          const parsed = JSON.parse(raw);
          setRegistries(Array.isArray(parsed) ? parsed : []);
        } else {
          setRegistries([]);
        }
      } catch {
        setRegistries([]);
      }
      setUseApi(false);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const saveRegistries = (next: GiftRegistry[]) => {
    setRegistries(next);
    if (!useApi) localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  };

  const addItem = () => {
    setForm(f => ({
      ...f,
      items: [...f.items, { id: Date.now().toString(), name: '', quantity: 1, notes: '' }],
    }));
  };

  const updateItem = (id: string, updates: Partial<RegistryItem>) => {
    setForm(f => ({
      ...f,
      items: f.items.map(i => (i.id === id ? { ...i, ...updates } : i)),
    }));
  };

  const removeItem = (id: string) => {
    setForm(f => ({ ...f, items: f.items.filter(i => i.id !== id) }));
  };

  const handleCreate = async () => {
    if (!form.title.trim()) {
      showToast('Please enter a title', 'error');
      return;
    }
    if (useApi) {
      const items = form.items.filter(i => i.name.trim()).map(i => ({ name: i.name.trim(), quantity: i.quantity, notes: i.notes || undefined }));
      const res = await giftRegistryApi.create({
        eventType: form.eventType,
        eventDate: form.eventDate,
        title: form.title.trim(),
        description: form.description.trim() || undefined,
        isActive: true,
        items: items.length ? items : undefined,
      });
      if (res?.success && res?.data) {
        setRegistries(prev => [...prev, normalizeApiRegistry(res.data)]);
        setForm(defaultForm);
        setShowCreate(false);
      } else {
        showToast(res?.error || 'Failed to create registry', 'error');
      }
      return;
    }
    const registry: GiftRegistry = {
      id: `reg-${Date.now()}`,
      eventType: form.eventType,
      eventDate: form.eventDate,
      title: form.title.trim(),
      description: form.description.trim(),
      items: form.items.filter(i => i.name.trim()).map(i => ({ ...i, name: i.name.trim() })),
      isActive: true,
      createdAt: new Date().toISOString(),
    };
    saveRegistries([...registries, registry]);
    setForm(defaultForm);
    setShowCreate(false);
  };

  const toggleActive = async (id: string) => {
    if (useApi) {
      const r = registries.find(x => x.id === id);
      if (!r) return;
      const res = await giftRegistryApi.update(id, { isActive: !r.isActive });
      if (res?.success && res?.data) {
        setRegistries(prev => prev.map(reg => reg.id === id ? normalizeApiRegistry(res.data) : reg));
      }
      return;
    }
    saveRegistries(registries.map(r => (r.id === id ? { ...r, isActive: !r.isActive } : r)));
  };

  const deleteRegistry = async (id: string) => {
    if (!(await askConfirm({ title: 'Delete this registry?', confirmLabel: 'Delete', danger: true }))) return;
    if (useApi) {
      const res = await giftRegistryApi.delete(id);
      if (res?.success) setRegistries(prev => prev.filter(r => r.id !== id));
      else showToast(res?.error || 'Failed to delete', 'error');
      return;
    }
    saveRegistries(registries.filter(r => r.id !== id));
  };

  const copyShareLink = (id: string) => {
    const url = `${window.location.origin}/gift-registry/${id}`;
    navigator.clipboard.writeText(url).then(() => showToast('Link copied!', 'success'));
  };

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto' }}>
      <div style={{
        background: 'linear-gradient(135deg, #ff9a56 0%, #ff6b35 100%)',
        borderRadius: '16px',
        padding: '24px',
        marginBottom: '24px',
        color: 'white',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
          <div>
            <h1 style={{ margin: '0 0 8px 0', fontSize: '28px' }}>🎁 Gift Registries</h1>
            <p style={{ margin: 0, opacity: 0.95, fontSize: '15px' }}>
              Create and manage gift lists for special occasions
            </p>
          </div>
          <button type="button" onClick={() => loadData(true)} disabled={refreshing} style={{ padding: '8px 16px', background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.4)', borderRadius: '8px', color: 'white', cursor: refreshing ? 'not-allowed' : 'pointer', fontSize: '14px' }}>
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      {loading && <p style={{ color: '#666', marginBottom: '12px' }}>Loading…</p>}
      {!loading && useApi && <p style={{ fontSize: '12px', color: '#28a745', marginBottom: '8px' }}>Synced with your account</p>}
      <div style={{ marginBottom: '20px' }}>
        <button
          onClick={() => setShowCreate(true)}
          style={{
            padding: '12px 24px',
            background: '#ff6b35',
            color: 'white',
            border: 'none',
            borderRadius: '10px',
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: '15px',
          }}
        >
          + Create registry
        </button>
      </div>

      {registries.length === 0 ? (
        <div style={{
          background: 'white',
          padding: '48px',
          borderRadius: '16px',
          textAlign: 'center',
          boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
        }}>
          <span style={{ fontSize: '48px' }}>🎁</span>
          <p style={{ color: '#666', marginTop: '15px' }}>Create a gift registry for birthdays, weddings, and more!</p>
          <button
            onClick={() => setShowCreate(true)}
            style={{
              marginTop: '16px',
              padding: '12px 24px',
              background: '#ff6b35',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            Create your first registry
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {registries.map(r => {
            const total = r.items.length;
            const purchased = r.items.filter(i => i.purchased).length;
            return (
              <div
                key={r.id}
                style={{
                  background: 'white',
                  borderRadius: '12px',
                  padding: '20px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                  borderLeft: `4px solid ${r.isActive ? '#ff6b35' : '#ccc'}`,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
                  <div>
                    <h3 style={{ margin: '0 0 4px 0', fontSize: '18px', color: '#333' }}>{r.title}</h3>
                    <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>
                      {r.eventType} · {new Date(r.eventDate).toLocaleDateString()}
                    </p>
                    <p style={{ margin: '6px 0 0 0', fontSize: '13px', color: '#888' }}>
                      {purchased}/{total} items purchased
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => copyShareLink(r.id)}
                      style={{ padding: '8px 14px', background: '#f0f0f0', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' }}
                    >
                      Copy link
                    </button>
                    <button
                      onClick={() => toggleActive(r.id)}
                      style={{ padding: '8px 14px', background: r.isActive ? '#fff3cd' : '#d4edda', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' }}
                    >
                      {r.isActive ? 'Pause' : 'Activate'}
                    </button>
                    <button
                      onClick={() => deleteRegistry(r.id)}
                      style={{ padding: '8px 14px', background: '#f8d7da', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', color: '#721c24' }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showCreate && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
        }}
        >
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '28px',
            maxWidth: '500px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
          }}
          >
            <h2 style={{ margin: '0 0 20px 0', fontSize: '20px', color: '#333' }}>Create gift registry</h2>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '14px' }}>Event type</label>
              <select
                value={form.eventType}
                onChange={e => setForm(f => ({ ...f, eventType: (e.target.value || 'Birthday') as typeof form.eventType }))}
                style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px' }}
              >
                {EVENT_TYPES.map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '14px' }}>Event date</label>
              <input
                type="date"
                value={form.eventDate}
                onChange={e => setForm(f => ({ ...f, eventDate: e.target.value }))}
                style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px' }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '14px' }}>Title *</label>
              <input
                type="text"
                value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="e.g. Sarah & Tom's Wedding"
                style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px' }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '14px' }}>Description (optional)</label>
              <textarea
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="A few words about your registry"
                rows={2}
                style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', resize: 'vertical' }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <label style={{ fontWeight: 600, fontSize: '14px' }}>Items</label>
                <button type="button" onClick={addItem} style={{ padding: '6px 12px', background: '#f0f0f0', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>+ Add</button>
              </div>
              {form.items.map((item, idx) => (
                <div key={item.id} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
                  <input
                    placeholder="Item name"
                    value={item.name}
                    onChange={e => updateItem(item.id, { name: e.target.value })}
                    style={{ flex: 2, padding: '8px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px' }}
                  />
                  <input
                    type="number"
                    min={1}
                    value={item.quantity}
                    onChange={e => updateItem(item.id, { quantity: parseInt(e.target.value, 10) || 1 })}
                    style={{ width: '60px', padding: '8px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px' }}
                  />
                  <input
                    placeholder="Notes"
                    value={item.notes}
                    onChange={e => updateItem(item.id, { notes: e.target.value })}
                    style={{ flex: 1, padding: '8px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px' }}
                  />
                  <button type="button" onClick={() => removeItem(item.id)} style={{ padding: '8px', background: '#f8d7da', border: 'none', borderRadius: '6px', cursor: 'pointer', color: '#721c24' }}>×</button>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
              <button
                onClick={() => { setShowCreate(false); setForm(defaultForm); }}
                style={{ padding: '10px 20px', background: '#f0f0f0', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                style={{ padding: '10px 20px', background: '#ff6b35', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 600 }}
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GiftRegistries;
